import { useContext, useEffect, useRef } from 'react';
import { UNSAFE_NavigationContext } from 'react-router-dom';

const DEFAULT_MESSAGE = 'You have unsaved changes. Are you sure you want to leave this page?';

const useConfirmNavigation = (shouldBlock, message = DEFAULT_MESSAGE) => {
  const navigationContext = useContext(UNSAFE_NavigationContext);
  const navigator = navigationContext?.navigator;
  const skipNextPopRef = useRef(false);

  useEffect(() => {
    if (!shouldBlock || !navigator?.block) {
      return undefined;
    }

    const unblock = navigator.block((tx) => {
      const confirmLeave = window.confirm(message);
      if (confirmLeave) {
        unblock();
        tx.retry();
      }
    });

    return unblock;
  }, [message, navigator, shouldBlock]);

  useEffect(() => {
    if (!shouldBlock || typeof window === 'undefined') {
      return undefined;
    }

    const restoreHistoryEntry = () => {
      skipNextPopRef.current = true;
      const goForward = () => {
        if (typeof window.history?.go === 'function') {
          window.history.go(1);
        } else if (typeof window.history?.forward === 'function') {
          window.history.forward();
        }
      };
      // Defer to ensure the browser finishes the original pop gesture.
      setTimeout(goForward, 0);
    };

    const handlePopState = () => {
      if (skipNextPopRef.current) {
        skipNextPopRef.current = false;
        return;
      }

      const confirmLeave = window.confirm(message);
      if (!confirmLeave) {
        restoreHistoryEntry();
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [message, shouldBlock]);

  useEffect(() => {
    if (!shouldBlock) {
      return undefined;
    }

    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = message;
      return message;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [message, shouldBlock]);
};

export default useConfirmNavigation;
